import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * The badge stack over the product gallery on the product page.
 *
 * It reuses the variant-update mechanism every other product block uses: the section is
 * re-rendered by the variant picker, the `productSelect` event carries the new markup, and this
 * element swaps in its own counterpart from it. No second event system, no client-side price or
 * badge maths — the badges of the new variant are whatever Liquid rendered for it.
 *
 * @extends {Component}
 */
class ProductMediaBadges extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
  }

  /** @type {Element | null} */
  #section = null;

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Product cards manage their own badges; ignore events bubbling out of one.
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector('product-media-badges');
        if (!next) return;

        this.replaceChildren(...next.cloneNode(true).childNodes);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-media-badges] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-media-badges')) {
  customElements.define('product-media-badges', ProductMediaBadges);
}
